<template>
  <div class="overflow-y-auto max-h-[calc(100vh-95px)]">
    <DataTable :columns="dtColumns" :rows="items" :click-fn="viewCandidate" :sort="sort" @sort-updated="handleNewSort">
      <template #td-name="slotProps">
        <router-link :to="`/contacts/${slotProps.row.id}`">
          <CandidateHeader
            :avatar="slotProps.row.initials"
            :header="slotProps.row.name"
            :subheader="slotProps.row.lead_source"
          />
        </router-link>
      </template>
      <template #td-email="slotProps">
        <router-link :to="`/contacts/${slotProps.row.id}`">
          <p>{{ slotProps.row.email }}</p>
          <p class="text-gray-500">{{ slotProps.row.phone }}</p>
        </router-link>
      </template>
      <template #td-license_status="slotProps">
        <CandidateLicenseBadge :license-status-label="slotProps.row.license_status" />
      </template>
      <template #td-actions="slotProps">
        <div @click.stop>
          <CandidateActionButtons :item="slotProps.row" />
        </div>
      </template>
    </DataTable>
    <Button v-if="total > items.length" variant="link" class="text-xs font-light text-gray-500" @click="loadMore">
      Load More
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import DataTable from '@/components/shared/DataTable/DeprecatedDataTable.vue';
import type { TableColumn, SortConfig } from '@/types/dataTable';
import CandidateHeader from '@/components/recruiting/Candidates/CandidateHeader.vue';
import CandidateLicenseBadge from '@/components/recruiting/Candidates/CandidateLicenseBadge.vue';
import CandidateActionButtons from '@/components/recruiting/Candidates/CandidateActionButtons.vue';
import type { CandidateEntity, SortOption, OrderOption } from '@/api/entity_types/recruiting/Candidate';
import { orderOptions, sortOptions } from '@/api/entity_types/recruiting/Candidate';
import { queryToEnum, stringToEnum } from '@/lib/typeParser';
import { formatShortDate } from '../../../utilities/format.ts';
import { Button } from '@/components/ui/button';

const router = useRouter();
const route = useRoute();
const { items, total, page } = defineProps<{
  items: CandidateEntity[];
  total: number;
  page: number;
}>();

const emit = defineEmits(['get-candidates']);

const sort = computed<SortConfig>(() => ({
  key: queryToEnum(route.query.sort, sortOptions) || '',
  order: queryToEnum(route.query.order, orderOptions) || 'asc',
}));

function viewCandidate(record: CandidateEntity) {
  router.push({ name: 'CandidateDetail', params: { id: record.id } });
}

async function setRouteQuery(sortKey?: SortOption, order?: OrderOption) {
  const queryParams = { ...route.query };
  queryParams['view'] = 'list';
  if (sortKey) {
    queryParams['sort'] = sortKey;
  }
  if (order) {
    queryParams['order'] = order;
  }
  await router.push({ path: 'contacts', query: queryParams });
}

async function loadMore() {
  emit('get-candidates', { page: page + 1 });
}

async function handleNewSort(newSort: SortConfig) {
  await setRouteQuery(stringToEnum(newSort.key, sortOptions), stringToEnum(newSort.order, orderOptions));
  emit('get-candidates', { page: 1 });
}

const dtColumns = computed<TableColumn[]>(() => [
  {
    label: 'Candidate',
    key: 'name',
    sortable: true,
  },
  {
    label: 'Email & Phone',
    key: 'email',
    sortable: false,
  },
  {
    label: 'License Status',
    key: 'license_status',
    sortable: true,
  },
  {
    label: 'Current Stage',
    key: 'stage',
    sortable: true,
  },
  {
    label: 'Applied',
    key: 'applied',
    formatter: val => formatShortDate(val) || '',
    sortable: true,
  },
  {
    label: 'Actions',
    key: 'actions',
    sortable: false,
  },
]);
</script>
